import { createApp } from "vue";
import { createWebHistory, createRouter } from "vue-router";
import { mask } from "vue-the-mask";

// styles
import "@fortawesome/fontawesome-free/css/all.min.css";
import "@/assets/styles/tailwind.css";

// mounting point
import App from "@/App.vue";

// layouts
import Admin from "@/layouts/Admin.vue";
import Auth from "@/layouts/Auth.vue";

// views for Admin layout
import Dashboard from "@/views/main/Dashboard.vue";
import Message from "@/views/main/Message.vue";
import change_password from "@/views/main/change_password.vue";
import Tables from "@/views/main/Tables.vue";
import Maps from "@/views/main/Maps.vue";
import Employees from "@/views/main/Employees.vue";
import Payables from "@/views/main/Payables.vue";
import Payroll from "@/views/main/Payroll.vue";
import TimeSheet from "@/views/main/Timesheet.vue";
import Resources from "@/views/main/Resources.vue";
import ResourcesSubmission from "@/views/main/file_uploader.vue";
import Reports from "@/views/main/Reports.vue";
import VendorInvoice from "@/views/main/VendorInvoice.vue";
import VendorInvoiceUpload from "@/views/main/VendorInvoiceUpload.vue";
import BudgetMonitoring from "@/views/main/BudgetMonitoring.vue";
import Enrollment from "@/views/main/Enrollment.vue";
import HireEmployees from "@/views/main/hired_employees.vue";
import VendorHiring from "@/views/main/Vendors.vue";
import Signature from "@/views/main/Signature.vue";
import PurchaseOrders from "@/views/main/PurchaseOrders.vue";
import SpendingHistory from "@/views/main/SpendingHistory.vue";
import EmployeeEnrollmentWizard from "@/views/main/EmployeeEnrollmentWizard.vue";

// views for Auth layout
import Login from "@/views/auth/Login.vue";
import Register from "@/views/auth/Register.vue";
import forgot_password from "@/views/auth/forgot_password.vue";
import VendorSignUp from "@/views/auth/VendorSignUp.vue";

// views without layouts
import Landing from "@/views/Landing.vue";
import Profile from "@/views/Profile.vue";

const routes = [
  {
    path: "/admin",
    redirect: "/main/dashboard",
    component: Admin,
    children: [
      {
        path: "/main/spending-history",
        component: SpendingHistory,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/dashboard",
        component: Dashboard,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/message",
        component: Message,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/resources",
        component: Resources,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/reports",
        component: Reports,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/purchase-order",
        component: PurchaseOrders,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/vendors-hiring",
        component: VendorHiring,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/hire_employees",
        component: HireEmployees,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/change_password",
        component: change_password,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/budget_monitoring",
        component: BudgetMonitoring,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/enrollment",
        component: Enrollment,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/signature",
        component: Signature,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/tables",
        component: Tables,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/maps",
        component: Maps,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/employees",
        component: Employees,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/payables",
        component: Payables,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/Resources_uploader",
        component: ResourcesSubmission,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/timesheets",
        component: TimeSheet,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/payroll",
        component: Payroll,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/vendor_invoice",
        component: VendorInvoice,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/vendor_invoice_upload",
        component: VendorInvoiceUpload,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/employee-enrollment",
        component: EmployeeEnrollmentWizard,
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: "/auth",
    redirect: "/auth/login",
    component: Auth,
    children: [
      {
        path: "/auth/login",
        component: Login,
      },
      {
        path: "/auth/register",
        component: Register,
      },
      {
        path: "/auth/forgot_password",
        component: forgot_password,
      },
      {
        path: "/sign-up",
        component: VendorSignUp,
      },
    ],
  },
  {
    path: "/landing",
    component: Landing,
  },
  {
    path: "/profile",
    component: Profile,
  },
  {
    path: "/",
    redirect: "/auth/login",
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/auth/login",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

function clearClientPortalSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("tokenExpiration");
  localStorage.removeItem("portal");
  localStorage.removeItem("user_type");
}

function isPublicRoute(path) {
  return (
    path === "/auth/login" ||
    path === "/auth/register" ||
    path === "/auth/forgot_password" ||
    path === "/sign-up"
  );
}

function isTokenValid(token, tokenExpiration) {
  if (!token || !tokenExpiration) return false;

  const expiration = Number(tokenExpiration);
  if (!Number.isFinite(expiration)) return false;

  return Date.now() < expiration;
}

function isVendorUser() {
  return localStorage.getItem("user_type") === "vendor";
}

function homeRouteForCurrentUser() {
  return isVendorUser() ? "/main/vendor_invoice_upload" : "/main/dashboard";
}

// Vendors only get their own invoice page + change password; everything else
// in /main/* is client/coordinator-only.
function isVendorAllowedRoute(path) {
  return (
    path === "/main/vendor_invoice_upload" ||
    path === "/main/change_password"
  );
}

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("token");
  const tokenExpiration = localStorage.getItem("tokenExpiration");
  const publicRoute = isPublicRoute(to.path);
  const validToken = isTokenValid(token, tokenExpiration);

  // Public auth pages should always load.
  if (publicRoute) {
    // Optional: if a valid session exists and user manually opens /auth/login,
    // send them to their role's landing page instead.
    if (validToken && to.path === "/auth/login") {
      return next(homeRouteForCurrentUser());
    }
    return next();
  }

  // Any protected page requires a valid token.
  if (validToken) {
    // Extend session on active use.
    const newExpirationTime = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem("tokenExpiration", String(newExpirationTime));

    if (isVendorUser() && !isVendorAllowedRoute(to.path)) {
      return next("/main/vendor_invoice_upload");
    }

    return next();
  }

  // Stale or missing session: clear it and go to login.
  clearClientPortalSession();
  return next("/auth/login");
});

const app = createApp(App);
app.directive("mask", mask);
app.use(router);
app.mount("#app");
